import {
  AccountTypeDisplayText,
  IAccountRespSchema,
  IContract,
  IDunnings,
  ILegalEntityResponseSchema,
} from '@monetize/types/app';
import { ApiListResponse, Maybe } from '@monetize/types/common';
import { toDateTimeShort } from '@monetize/utils/core';
import {
  useGetGatewayAccountsByAccount,
  useGetPaymentMethodsByAccount,
  useGetPaymentsByAccount,
} from '../../../api/accountsService';
import { useGetBillGroups } from '../../../api/billGroupService';
import { useGetAccountContacts } from '../../../api/contactsService';
import { useGetContracts, useGetQuoteList } from '../../../api/cpqService';
import { useGetGatewayCustomerAccount } from '../../../api/paymentsService';
import { useGetById } from '../../../api/queryUtils';
import { AccountStatusDisplayText } from '../../../constants/accounts';
import { getAddress } from '../../../utils/address';

type RowValue = {
  label: string;
  account1Value?: string | number | null;
  account2Value?: string | number | null;
};

/**
 * Get core account field table row values
 */
export const useTableRowValues = (
  mainAccount: IAccountRespSchema,
  otherAccount?: Maybe<IAccountRespSchema>,
) => {
  const { data: dunningProcess1 } = useGetById<IDunnings>(
    'dunnings',
    mainAccount.defaultDunningProcessId || '',
    { enabled: !!mainAccount.defaultDunningProcessId },
  );
  const { data: dunningProcess2 } = useGetById<IDunnings>(
    'dunnings',
    otherAccount?.defaultDunningProcessId || '',
    { enabled: !!otherAccount?.defaultDunningProcessId },
  );

  const { data: legalEntity1 } = useGetById<ILegalEntityResponseSchema>(
    'legalEntities',
    mainAccount.defaultLegalEntityId,
  );
  const { data: legalEntity2 } = useGetById<ILegalEntityResponseSchema>(
    'legalEntities',
    otherAccount?.defaultLegalEntityId || '',
    { enabled: !!otherAccount?.defaultLegalEntityId },
  );

  const values: RowValue[] = [
    {
      label: 'Created',
      account1Value: toDateTimeShort(mainAccount.createDate, 'userTimezone'),
      account2Value: otherAccount
        ? toDateTimeShort(otherAccount.createDate, 'userTimezone')
        : null,
    },
    {
      label: 'Status',
      account1Value: AccountStatusDisplayText[mainAccount.status],
      account2Value: otherAccount
        ? AccountStatusDisplayText[otherAccount?.status]
        : null,
    },
    {
      label: 'AccountType',
      account1Value: AccountTypeDisplayText[mainAccount.accountType],
      account2Value: otherAccount
        ? AccountTypeDisplayText[otherAccount?.accountType]
        : null,
    },
    {
      label: 'Is Test Account',
      account1Value: mainAccount.isTestAccount ? 'Yes' : 'No',
      account2Value: otherAccount?.isTestAccount ? 'Yes' : 'No',
    },
    {
      label: 'Custom ID',
      account1Value: mainAccount.customId,
      account2Value: otherAccount?.customId,
    },
    {
      label: 'Default Legal Entity',
      account1Value: legalEntity1?.name,
      account2Value: legalEntity2?.name,
    },
    {
      label: 'Default Dunning Process',
      account1Value: dunningProcess1?.name,
      account2Value: dunningProcess2?.name,
    },
    {
      label: 'Account-level CCs',
      account1Value: mainAccount.ccEmails,
      account2Value: otherAccount?.ccEmails,
    },
    {
      label: 'Billing Address',
      account1Value: getAddress(mainAccount.billingAddress).fullAddress,
      account2Value: getAddress(otherAccount?.billingAddress).fullAddress,
    },
    {
      label: 'Shipping Address',
      account1Value: getAddress(mainAccount.shippingAddress).fullAddress,
      account2Value: getAddress(otherAccount?.shippingAddress).fullAddress,
    },
  ];
  return values;
};

const PAGER = { page: 0, rows: 1 };
export function useGetAccountRecords(accountId?: Maybe<string>) {
  accountId = accountId || '';

  const { data: contactCount } = useGetAccountContacts<number>(
    { config: PAGER, accountId },
    { enabled: !!accountId, select: (data) => data.totalElements ?? 0 },
  );
  const { data: billGroupCount } = useGetBillGroups<number>(
    { config: PAGER, filters: { accountId } },
    { enabled: !!accountId, select: (data) => data.totalElements ?? 0 },
  );
  const { data: quoteCount } = useGetQuoteList<number>(
    { config: PAGER, accountId },
    { enabled: !!accountId, select: (data) => data.totalElements ?? 0 },
  );

  const { data: contractCount } = useGetContracts<
    ApiListResponse<IContract>,
    number
  >(
    { config: PAGER, filters: { accountId } },
    { enabled: !!accountId, select: (data) => data.totalElements ?? 0 },
  );
  const { data: paymentMethodCount } = useGetPaymentMethodsByAccount<number>(
    { config: PAGER, accountId },
    { enabled: !!accountId, select: (data) => data.totalElements ?? 0 },
  );
  const { data: paymentCount } = useGetPaymentsByAccount<number>(
    { config: PAGER, accountId },
    { enabled: !!accountId, select: (data) => data.totalElements ?? 0 },
  );
  const { data: paymentGatewayAccount } = useGetGatewayAccountsByAccount(
    accountId,
    { enabled: !!accountId, select: (value) => value?.[0] },
  );

  const { data: gatewayCustomerAccount } = useGetGatewayCustomerAccount(
    paymentGatewayAccount?.paymentGatewayId || '',
    paymentGatewayAccount?.gatewayCustomerId || '',
    { enabled: !!paymentGatewayAccount },
  );

  return {
    accountId,
    contactCount: contactCount ?? 0,
    billGroupCount: billGroupCount ?? 0,
    quoteCount: quoteCount ?? 0,
    contractCount: contractCount ?? 0,
    paymentMethodCount: paymentMethodCount ?? 0,
    paymentCount: paymentCount ?? 0,
    gatewayCustomerAccount,
  };
}

/**
 * Get related record field table row values
 */
export function useRelatedRecordValues(
  mainAccount: IAccountRespSchema,
  otherAccount?: Maybe<IAccountRespSchema>,
) {
  const mainAccountRecords = useGetAccountRecords(mainAccount.id);
  const otherAccountRecords = useGetAccountRecords(otherAccount?.id);

  const values: RowValue[] = [];

  if (mainAccountRecords.contactCount || otherAccountRecords.contactCount) {
    values.push({
      label: 'Contacts',
      account1Value: mainAccountRecords.contactCount,
      account2Value: otherAccountRecords.contactCount,
    });
  }
  if (mainAccountRecords.billGroupCount || otherAccountRecords.billGroupCount) {
    values.push({
      label: 'Bill Groups',
      account1Value: mainAccountRecords.billGroupCount,
      account2Value: otherAccountRecords.billGroupCount,
    });
  }
  if (mainAccountRecords.quoteCount || otherAccountRecords.quoteCount) {
    values.push({
      label: 'Quotes',
      account1Value: mainAccountRecords.quoteCount,
      account2Value: otherAccountRecords.quoteCount,
    });
  }
  if (mainAccountRecords.contractCount || otherAccountRecords.contractCount) {
    values.push({
      label: 'Contracts',
      account1Value: mainAccountRecords.contractCount,
      account2Value: otherAccountRecords.contractCount,
    });
  }
  if (
    mainAccountRecords.paymentMethodCount ||
    otherAccountRecords.paymentMethodCount
  ) {
    values.push({
      label: 'Payment Methods',
      account1Value: mainAccountRecords.paymentMethodCount,
      account2Value: otherAccountRecords.paymentMethodCount,
    });
  }
  if (mainAccountRecords.paymentCount || otherAccountRecords.paymentCount) {
    values.push({
      label: 'Payments',
      account1Value: mainAccountRecords.paymentCount,
      account2Value: otherAccountRecords.paymentCount,
    });
  }
  if (
    mainAccountRecords.gatewayCustomerAccount ||
    otherAccountRecords.gatewayCustomerAccount
  ) {
    values.push({
      label: 'Payment Gateway Customer',
      account1Value: mainAccountRecords.gatewayCustomerAccount
        ? `${mainAccountRecords.gatewayCustomerAccount?.gatewayAccount?.name} (${mainAccountRecords.gatewayCustomerAccount?.gatewayAccount?.id})`
        : null,
      account2Value: otherAccountRecords.gatewayCustomerAccount
        ? `${otherAccountRecords.gatewayCustomerAccount?.gatewayAccount?.name} (${otherAccountRecords.gatewayCustomerAccount?.gatewayAccount?.id})`
        : null,
    });
  }

  return {
    mainAccountRecords,
    otherAccountRecords,
    values,
  };
}
